/**
 * "New tenant" (vendor.admin, D13): its id (chosen once, a key segment), its
 * name and its first admin's email — the tenant is created with its default
 * groups and that admin, whose invitation is sent separately.
 */
import { emailSchema, tenantIdSchema } from '@acp/api-contract/tenancy.js';
import { type FormEvent, useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Field, FieldDescription, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';

export type NewTenant = { readonly tenantId: string; readonly name: string; readonly adminEmail: string };

export type CreateTenantDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSubmit: (tenant: NewTenant) => void;
};

function CreateTenantForm({ onOpenChange, pending, error, onSubmit }: Omit<CreateTenantDialogProps, 'open'>) {
  const [tenantId, setTenantId] = useState('');
  const [name, setName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const idError = tenantId === '' || tenantIdSchema.safeParse(tenantId.trim()).success ? null : '3–40 lowercase letters, digits or hyphens, starting with a letter.';
  const emailError = adminEmail === '' || emailSchema.safeParse(adminEmail.trim()).success ? null : 'Not an email address.';
  const ready = tenantId.trim() !== '' && name.trim() !== '' && adminEmail.trim() !== '' && idError === null && emailError === null;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready || pending) return;
    onSubmit({ tenantId: tenantId.trim(), name: name.trim(), adminEmail: adminEmail.trim() });
  };
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>New tenant</DialogTitle>
        <DialogDescription>Creates the tenant with its default groups (Admin, Producer/Editor, Operator, Viewer) and its first admin.</DialogDescription>
      </DialogHeader>
      <Field invalid={idError !== null}>
        <Label htmlFor="tenant-id">Tenant id</Label>
        <Input id="tenant-id" value={tenantId} onChange={(e) => setTenantId(e.target.value)} disabled={pending} aria-invalid={idError !== null} placeholder="acme-tv" autoFocus />
        <FieldDescription>Permanent: it is part of every key and every node’s DNS name.</FieldDescription>
        <FieldError>{idError}</FieldError>
      </Field>
      <Field>
        <Label htmlFor="tenant-name">Name</Label>
        <Input id="tenant-name" value={name} onChange={(e) => setName(e.target.value)} disabled={pending} />
      </Field>
      <Field invalid={emailError !== null}>
        <Label htmlFor="tenant-admin">First admin’s email</Label>
        <Input id="tenant-admin" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} disabled={pending} aria-invalid={emailError !== null} />
        <FieldError>{emailError}</FieldError>
      </Field>
      <FieldError>{error}</FieldError>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!ready || pending} data-testid="create-tenant-submit">
          {pending ? 'Creating…' : 'Create'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function CreateTenantDialog({ open, ...rest }: CreateTenantDialogProps) {
  return (
    <Dialog open={open} onOpenChange={rest.onOpenChange}>
      <DialogContent data-testid="create-tenant-dialog">
        <CreateTenantForm {...rest} />
      </DialogContent>
    </Dialog>
  );
}
